import type { UploadReferenceImageResponse } from '@historyjki/shared';

const API_BASE = '/api';

class ApiClient {
  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = `${API_BASE}${path}`;
    const headers: Record<string, string> = {};

    if (body && !(body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(url, {
      method,
      headers,
      credentials: 'include',
      body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new ApiError(response.status, error.message || 'Request failed');
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path);
  }

  post<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>('POST', path, body);
  }

  patch<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>('PATCH', path, body);
  }

  delete<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path);
  }

  upload<T>(path: string, file: File, metadata?: Record<string, string>): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);
    if (metadata) {
      Object.entries(metadata).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }
    return this.request<T>('POST', path, formData);
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const api = new ApiClient();

/**
 * Resolve a user-facing message from a thrown error: the `ApiError`'s message
 * when available, otherwise the provided fallback. Centralises the
 * `err instanceof ApiError ? err.message : fallback` triplet that recurs at
 * every catch site.
 */
export function errMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

/** Show an error toast for a thrown error, using `errMessage` for the text. */
export function toastError(
  showToast: (message: string, type: 'error') => void,
  err: unknown,
  fallback: string,
): void {
  showToast(errMessage(err, fallback), 'error');
}

/**
 * Upload a reference image for an owner (`project`, `story_element`, or
 * `page`). Wraps the `/upload/reference-image` multipart endpoint so callers
 * don't reconstruct the same FormData metadata shape, and returns the typed
 * response (callers may read `reference_image.r2_key`).
 */
export function uploadReferenceImage(
  ownerType: string,
  ownerId: string,
  file: File,
): Promise<UploadReferenceImageResponse> {
  return api.upload<UploadReferenceImageResponse>('/upload/reference-image', file, {
    owner_type: ownerType,
    owner_id: ownerId,
  });
}
